import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { AppBar, Avatar, Box, ButtonBase, IconButton, Toolbar, Typography, InputBase, Badge, List, ListItem, ListItemText, Paper, Button, useMediaQuery } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import SchoolIcon from "@mui/icons-material/School";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { AccountPopover } from "./account-popover";
import { NotificationsPopover } from "./notifications-popover";
import ChatIcon from "@mui/icons-material/Chat";
import { useAuth } from "../../hooks/use-auth";
import colors from './../../../src/theme/colors.js';

const DashboardNavbarRoot = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== "openSide",
})(({ theme }) => ({
  backgroundColor: "#ffffff",
  // MUI AppBar est déjà width:100% par défaut — pas besoin de left/right ici
  zIndex: theme.zIndex.drawer + 1,
  boxShadow: "0px 1px 8px rgba(0,0,0,0.08)",
  borderBottom: "1px solid rgba(0,0,0,0.06)",
  [theme.breakpoints.up("lg")]: {
    // Approche officielle MUI pour persistent drawer + AppBar
    width: "calc(100% - 240px)",
    marginLeft: "240px",
  },
}));

const AccountButton = () => {
  const { user } = useAuth();
  const anchorRef = useRef(null);
  const [openPopover, setOpenPopover] = useState(false);
  const handleOpenPopover = () => setOpenPopover(true);
  const handleClosePopover = () => setOpenPopover(false);

  return (
    <>
      <Box
        component={ButtonBase}
        onClick={handleOpenPopover}
        ref={anchorRef}
        sx={{
          alignItems: "center",
          display: "flex",
          ml: 0,
          mr: 2,
        }}
      >
        <Avatar
          src={user.avatar || undefined}
          sx={{
            height: 40,
            width: 40,
            mx: 0,
            backgroundColor: "#9E9E9E",
            color: "white",
            fontWeight: 700,
            fontSize: "1rem",
          }}
        >
          {user.firstname ? user.firstname.charAt(0).toUpperCase() : "?"}
        </Avatar>
        <Box sx={{ ml: 2, mr: 3 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: "bold",
              color: "black",
              fontSize: "0.9rem",
            }}
          >
            {user.firstname} {user.lastname}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: "black",
              fontSize: "0.8rem",
            }}
          >
            {user.id_service === 0 ? "Administrateur" : "Expert"}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: colors.primary,
              fontSize: "0.8rem",
              fontWeight: "bold",
            }}
          >
            {globalThis.localStorage.getItem('tenant').toUpperCase()}
          </Typography>
        </Box>
      </Box>
      <AccountPopover
        anchorEl={anchorRef.current}
        onClose={handleClosePopover}
        open={openPopover}
      />
    </>
  );
};

export const DashboardNavbar = (props) => {
  const { onOpenSidebar, open, openSide, setOpenSide, ...other } = props;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const { tenant } = useParams();
  const [messagesNotRead, setMessagesNotRead] = useState(0);
  const paperRef = useRef(null);
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredResults, setFilteredResults] = useState([]);
  const [showAllResults, setShowAllResults] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const defaultProfileImage = "https://via.placeholder.com/150";

  const getPermissionsByRole = async (roles_id) => {
    const url = `${process.env.REACT_APP_BACK}/subdomain/${localStorage.getItem('tenant')}/api/permission-verification/get/`;
    const values = { roles_id };
    const accessToken = localStorage.getItem("accessToken");

    try {
      const resp = await fetch(url, {
        method: "POST",
        body: JSON.stringify(values),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!resp.ok) throw new Error(`HTTP error! Status: ${resp.status}`);
      const json = await resp.json();
      setPermissions(json.permissions);
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  };

  const hasViewPermission = () => {
    return permissions.some(
      (p) => p.component === "Administrateurs" && p.permission === "Voir"
    );
  };

  const hasUpdatePermission = () => {
    return permissions.some(
      (p) => p.component === "Administrateurs" && p.permission === "Mettre à jour"
    );
  };

  useEffect(() => {
    if (user?.roles_id) getPermissionsByRole(user.roles_id);
    const lastSearchQuery = localStorage.getItem('lastSearchQuery');
    if (lastSearchQuery) {
      setSearchQuery(lastSearchQuery);
    }
  }, [user]);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    localStorage.setItem('lastSearchQuery', query);

    if (query.length >= 3) {
      try {
        const values = {
          keyword: query,
        };
        const tenant=globalThis.localStorage.getItem('tenant');
        const accessToken = globalThis.localStorage.getItem(`accessToken_${tenant}`);
        const url = `${process.env.REACT_APP_BACK}/subdomain/${globalThis.localStorage.getItem('tenant')}/api/recherche/`;

        const response = await fetch(url, {
          method: 'POST',
          body: JSON.stringify(values),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`
          }
        });

        const data = await response.json();
        if (response.ok) {
          let results = data.results || {};

          if (!hasViewPermission() && results["Administrateurs"]) {
            delete results["Administrateurs"];
          }

          const filteredResults = Object.entries(results).map(([category, items]) => ({
            category,
            items: items.map((item) => {
              const isAuditCategory = category === "EPP";
              const isAdminCategory = category === "Administrateurs";
              const isUsersCategory = category === "Utilisateurs";

              return {
                firstname: isAuditCategory ? undefined : item.firstname || "Prénom inconnu",
                lastname: isAuditCategory ? undefined : item.lastname || "Nom inconnu",
                email: isAuditCategory ? undefined : item.email || "Email non disponible",
                avatar: isAdminCategory ? item.avatar : defaultProfileImage,
                role_name: isAdminCategory ? item.role_name : undefined,
                role_id : isAdminCategory ? item.role_id : undefined,
                name: isAuditCategory ? item.name : undefined,
                IDD: isAdminCategory ? item.id : (isAuditCategory ? item.IDD : undefined),
                type: isAuditCategory ? (item.type === "tcs" ? "TCS" : item.type === "audit" ? "Audit" : item.type) : undefined,
                image_link: isAuditCategory ? item.image_link : undefined,
                id: (isAuditCategory || isAdminCategory) ? item.id : undefined,
                Id: isUsersCategory ? item.id : undefined,
                url: category === "Utilisateurs",
                icon: category === "Admins" ? <AdminPanelSettingsIcon /> : <SchoolIcon />,
              };
            }),
          }));
          setFilteredResults(filteredResults);
        } else {
          console.error("Erreur lors de la recherche:", data.msg);
        }
      } catch (error) {
        console.error("Erreur réseau:", error);
      }
    } else {
      setFilteredResults([]);
    }
  };

  const handleClickOutside = (event) => {
    if (
      paperRef.current &&
      !paperRef.current.contains(event.target) &&
      !event.target.closest(".MuiInputBase-root")
    ) {
      setIsSearchFocused(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getMaxResults = (filteredCategoriesCount) => {
    switch (filteredCategoriesCount) {
      case 1:
        return 7;
      case 2:
        return 4;
      case 3:
        return 2;
      default:
        return 7;
    }
  };

  const filteredResultsWithLimit = filteredResults
    .filter((result) => result.items && result.items.length > 0)
    .map((result) => {
      const filteredCategoriesCount = filteredResults.filter((r) => r.items && r.items.length > 0).length;
      const maxResults = getMaxResults(filteredCategoriesCount);

      return {
        ...result,
        items: result.items.slice(0, maxResults),
      };
    });

    const handleItemClick = (item) => {
      setIsSearchFocused(false); // Ajoutez cette ligne
      
      if (item.type === "Audit") {
        navigate(`/${globalThis.localStorage.getItem('tenant')}/dashboard/epp/audits/${item.id}`);
        setSearchQuery("");
        setFilteredResults([]);
      } else if (item.url == 1) {
        navigate(`/${globalThis.localStorage.getItem('tenant')}/dashboard/customers/${item.Id}`);
        setSearchQuery("");
        setFilteredResults([]);
      } else if (item.type == "TCS") {
        navigate(`/${globalThis.localStorage.getItem('tenant')}/dashboard/epp/tcs/${item.id}`);
        setSearchQuery("");
        setFilteredResults([]);
      } else if (hasUpdatePermission()) {
        navigate(`/${globalThis.localStorage.getItem('tenant')}/dashboard/admins/update/${item.IDD}`, {
          state: {
            admin: {
              id: item.id,
              firstname: item.firstname,
              lastname: item.lastname,
              email: item.email,
              avatar: item.avatar,
              role_id: item.role_id,
              role_name: item.role_name
            }
          }
        });
      }
    };

  const truncateText = (text, maxLength = 10) => {
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  const highlightText = (text, query) => {
    if (typeof text !== 'string' || !query) return text;

    const regex = new RegExp(`(${query})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={index} style={{ fontWeight: "bold" }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <DashboardNavbarRoot openSide={openSide} {...other}>
      <Toolbar
        disableGutters
        sx={{
          backgroundColor: "white",
          height: "64px",
          minHeight: "64px",
          display: "flex",
          justifyContent: "space-between",
          px: 2,
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {!isLgUp && (
            <IconButton
              onClick={onOpenSidebar}
              sx={{ color: "#8282CA", "&:hover": { backgroundColor: "rgba(130,130,202,0.1)" } }}
            >
              <MenuIcon />
            </IconButton>
          )}
        <Box sx={{ position: "relative" }}>
          <Box sx={{
            display: "flex",
            alignItems: "center",
            borderRadius: "8px",
            border: "1px solid #ccc",
            ml: "20px",
            padding: "6px 12px",
            "&:focus-within": { 
              borderColor: colors.primary, 
              boxShadow: "0 0 8px rgba(96, 96, 184, 0.3)" 
            } 
          }}>
            <SearchIcon
              sx={{
                color: "gray",
                mr: 1,
                fontSize: isLgUp ? "28px" : "24px",
              }}
            />
            <InputBase
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              sx={{
                flex: 4,
                color: "black",
                fontSize: isLgUp ? "1rem" : "0.9rem",
                "&:focus": {
                  outline: "none",
                },
                width: isLgUp ? "300px" : "250px",
              }}
            />
          </Box>

          {isSearchFocused && searchQuery && filteredResultsWithLimit.length > 0 && (
            <Paper
              ref={paperRef}
              sx={{
                position: "absolute",
                top: "60px",
                left: 0,
                width: isLgUp ? "450px" : "350px",
                maxHeight: "500px",
                overflowY: "auto",
                zIndex: 10,
                ml: "20px",
                borderRadius: "12px",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
                backgroundColor: "#ffffff",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: "-10px",
                  left: "20px",
                  width: 0,
                  height: 0,
                  borderLeft: "10px solid transparent",
                  borderRight: "10px solid transparent",
                  borderBottom: "10px solid #ffffff",
                },
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <List>
                {filteredResultsWithLimit.map((result, index) =>
                  result.items ? (
                    <Box key={index} sx={{ marginBottom: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          padding: "8px 16px",
                          backgroundColor: "#f5f5f5",
                          marginBottom: "4px",
                          color: colors.primary,
                          fontSize: "0.85rem",
                          fontFamily: "'Roboto', sans-serif",
                        }}
                      >
                        {result.category}
                      </Typography>

                      {(showAllResults ? result.items : result.items.slice(0, 7)).map((item, i) => (
                        <ListItem
                          key={i}
                          button={hasUpdatePermission()}
                          onClick={() => hasUpdatePermission() && handleItemClick(item)}
                          sx={{ 
                            cursor: hasUpdatePermission() ? 'pointer' : 'default',
                            padding: "8px 16px",
                            '&:hover': {
                              backgroundColor: hasUpdatePermission() ? '#f5f5f5' : 'transparent'
                            }
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, marginY: -1 }}>
                                {item.image_link && (
                                  <img
                                    src={item.image_link}
                                    alt="EPP"
                                    style={{
                                      height: 40,
                                      width: 40,
                                      marginRight: "12px",
                                      objectFit: "cover",
                                      borderRadius: "5%",
                                    }}
                                  />
                                )}
                                {!item.image_link && (
                                  <Avatar
                                    src={item.avatar && item.avatar !== defaultProfileImage ? item.avatar : undefined}
                                    sx={{
                                      height: 40,
                                      width: 40,
                                      marginRight: "12px",
                                      backgroundColor: "#9E9E9E",
                                      color: "white",
                                      fontWeight: 700,
                                      fontSize: "1rem",
                                    }}
                                  >
                                    {item.firstname ? item.firstname.charAt(0).toUpperCase() : "?"}
                                  </Avatar>
                                )}

                                <Box sx={{ display: "flex", flexDirection: "column" }}>
                                  {item.firstname && item.lastname && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.8,
                                      }}
                                    >
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontSize: "0.9rem",
                                          fontWeight: 500,
                                          color: "#333",
                                        }}
                                      >
                                        {highlightText(item.firstname, searchQuery)}
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontSize: "0.9rem",
                                          fontWeight: 500,
                                          color: "#333",
                                        }}
                                      >
                                        {highlightText(truncateText(item.lastname), searchQuery)}
                                      </Typography>
                                    </Box>
                                  )}

                                  {item.email && (
                                    <Typography
                                      variant="body2"
                                      sx={{ fontSize: "0.8rem", color: "gray" }}
                                    >
                                      {highlightText(item.email, searchQuery)}
                                    </Typography>
                                  )}

                                  {item.name && (
                                    <>
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontSize: "0.9rem",
                                          fontWeight: 500,
                                          color: "#333",
                                        }}
                                      >
                                        {highlightText(item.name, searchQuery)}
                                      </Typography>

                                      <Typography
                                        variant="body2"
                                        sx={{ fontSize: "0.8rem", color: "gray" }}
                                      >
                                        {highlightText(item.type, searchQuery)}
                                      </Typography>
                                    </>
                                  )}
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </Box>
                  ) : null
                )}

                {!showAllResults &&
                  filteredResults.some((result) => result.items?.length > 8) && (
                    <Button
                      fullWidth
                      onClick={() =>
                        navigate("/detailed-results", {
                          state: {
                            searchQuery: searchQuery,
                            results: filteredResults.map((result) => ({
                              id: result.id,
                              title: result.category || "Non spécifié",
                              items: result.items?.map((item) => ({
                                name: item.name,
                                email: item.email,
                                firstname: item.firstname,
                                avatar: item.avatar,
                                type: item.type,
                                image_link: item.image_link,
                                lastname: item.lastname,
                                image: item.image,
                                Id: item.Id,
                                id: item.id
                              })),
                            })),
                          },
                        })
                      }
                      sx={{ 
                        textTransform: "none",
                        fontSize: "0.85rem",
                        padding: "8px 16px",
                      }}
                      startIcon={<SearchIcon sx={{ fontSize: "1rem" }} />}
                    >
                      Voir les résultats détaillés
                    </Button>
                  )}
              </List>
            </Paper>
          )}
        </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <NotificationsPopover />
          <Box 
            component={ButtonBase} 
            sx={{ 
              mr: 2,
              "&:hover": {
                backgroundColor: "rgba(130, 130, 202, 0.1)",
                borderRadius: "50%",
              },
              padding: "8px",
            }}
          >
            <Badge
              badgeContent={messagesNotRead}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  right: 2,
                  top: 8,
                  border: "2px solid #fff",
                  padding: "0 4px",
                  fontSize: "0.7rem",
                },
              }}
            >
              <ChatIcon
                sx={{
                  fontSize: "28px",
                  color: "#8282CA",
                }}
                onClick={() =>
                  navigate(
                    `/${localStorage.getItem("tenant")}/dashboard/discussion`
                  )
                }
              />
            </Badge>
          </Box>
          <AccountButton />
        </Box>
      </Toolbar>
    </DashboardNavbarRoot>
  );
};

DashboardNavbar.propTypes = {
  onOpenSidebar: PropTypes.func,
};